// Thin wrapper over the shared PostHog snippet (see components/Analytics.tsx).
// Every event is stamped with the standard attribution properties the build brief
// asks for (hostname, page path, trade, utm_*, gclid/msclkid) so the new preview
// journey is measurable without re-reading the URL at each call site.

import { recordSearch } from "./pageContext";

type Props = Record<string, unknown>;

function param(search: URLSearchParams, key: string): string {
  return search.get(key) || "";
}

/** Attribution + page context attached to every tracked event. */
export function baseProps(extra: Props = {}): Props {
  if (typeof window === "undefined") return { ...extra };
  const search = new URLSearchParams(window.location.search);
  return {
    hostname: window.location.hostname.replace(/^www\./, ""),
    page_path: window.location.pathname,
    utm_source: param(search, "utm_source"),
    utm_medium: param(search, "utm_medium"),
    utm_campaign: param(search, "utm_campaign"),
    utm_term: param(search, "utm_term"),
    utm_content: param(search, "utm_content"),
    gclid: param(search, "gclid"),
    msclkid: param(search, "msclkid"),
    ...extra,
  };
}

/** Fire a Google Ads conversion (gtag loaded in Analytics.tsx). No-op when blocked. */
export function googleConversion(sendTo: string): void {
  if (typeof window === "undefined") return;
  const g = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof g !== "function") return;
  try {
    g("event", "conversion", { send_to: sendTo });
  } catch {
    /* tracking must never throw into the UI */
  }
}

type Posthog = {
  capture?: (e: string, p: Props) => void;
  identify?: (id: string, set?: Props) => void;
  get_distinct_id?: () => string;
  get_session_id?: () => string;
};
function getPosthog(): Posthog | undefined {
  return (window as unknown as { posthog?: Posthog }).posthog;
}

/** Current PostHog distinct_id + session_id, to forward to the worker so the
 *  server-emitted signup_completed attributes to THIS browser person and session
 *  (the worker uses `body.ph_distinct_id || email`). Without it the worker falls
 *  back to email and the event spawns a separate, journey-less person. Returns
 *  empty strings when PostHog is blocked — the worker then falls back to email. */
export function phContext(): { ph_distinct_id: string; ph_session_id: string } {
  const ph = typeof window === "undefined" ? undefined : getPosthog();
  let ph_distinct_id = "";
  let ph_session_id = "";
  try {
    if (ph && typeof ph.get_distinct_id === "function") ph_distinct_id = ph.get_distinct_id() || "";
  } catch {
    /* analytics must never throw into the UI */
  }
  try {
    if (ph && typeof ph.get_session_id === "function") ph_session_id = ph.get_session_id() || "";
  } catch {
    /* analytics must never throw into the UI */
  }
  return { ph_distinct_id, ph_session_id };
}

/** Link the anonymous browser person to the signed-up email so the whole pre-signup
 *  journey (source, builder steps) merges into one PostHog person. The server emits
 *  signup_completed with distinct_id = the lowercased email; calling identify() with
 *  that same email merges the anon profile into it. Without this the email lands on a
 *  separate server-side person and every event before signup stays unattributed.
 *  Email MUST match the server's lowercased value. Retries until the afterInteractive
 *  snippet loads; gives up after ~5s if PostHog is blocked (consent/ad-blocker). */
export function identify(email: string, set: Props = {}, attempt = 0): void {
  if (typeof window === "undefined" || !email) return;
  const ph = getPosthog();
  if (ph && typeof ph.identify === "function") {
    try {
      ph.identify(email, set);
    } catch {
      /* analytics must never throw into the UI */
    }
    return;
  }
  if (attempt >= 25) return; // ~5s — PostHog blocked, give up
  setTimeout(() => identify(email, set, attempt + 1), 200);
}

// Events fired before the PostHog snippet finishes loading (afterInteractive) would
// otherwise be dropped — notably above-the-fold view events like hero_viewed, which
// fire on mount and race the loader. Queue them and flush once capture() is available.
const pending: [string, Props][] = [];
let flushScheduled = false;
function flushPending(attempt = 0): void {
  const ph = getPosthog();
  if (!ph || typeof ph.capture !== "function") {
    if (attempt >= 25) {
      pending.length = 0; // PostHog blocked (consent/ad-blocker) — give up after ~5s
      flushScheduled = false;
      return;
    }
    setTimeout(() => flushPending(attempt + 1), 200);
    return;
  }
  flushScheduled = false;
  while (pending.length) {
    const [e, p] = pending.shift()!;
    try {
      ph.capture(e, p);
    } catch {
      /* analytics must never throw into the UI */
    }
  }
}

// Mirror key learning-milestone events to Microsoft UET (Bing) so Microsoft Ads can
// optimise toward engaged/useful traffic, not just clicks. Create matching "event goals"
// in Microsoft Ads (Tools -> Conversion tracking) keyed on these action names. uetq queues
// pushes until bat.js loads, so this is safe to call any time.
const UET_EVENTS: Record<string, string> = {
  lp_cta_clicked: "lp_engaged",
  hero_find_clicked: "find_template",
  hero_build_clicked: "build_rams",
  intent_find_template_clicked: "find_template",
  intent_build_rams_clicked: "build_rams",
  intent_check_rams_clicked: "check_rams",
  intent_template_search_submitted: "template_search",
  template_search: "template_search",
  template_search_performed: "template_search",
  template_request_submitted: "template_request",
  template_result_clicked: "reached_template",
  template_card_clicked: "reached_template",
  builder_trade_selected: "builder_engaged",
  builder_category_selected: "builder_engaged",
  builder_task_selected: "builder_engaged",
  rejection_flow_started: "rejection_flow_started",
  rejection_fix_generated: "rejection_fix_generated",
  report_downloaded: "rams_generated",
};
function uetEvent(action: string): void {
  try {
    const q = (window as unknown as { uetq?: { push: (...a: unknown[]) => void } }).uetq;
    if (q && typeof q.push === "function") q.push("event", action, {});
  } catch {
    /* analytics must never throw into the UI */
  }
}

/** Fire a PostHog event with standard attribution props merged in.
 *  If PostHog isn't ready yet, the event is queued and flushed once it loads.
 *  Milestone events are also mirrored to Microsoft UET for ad optimisation. */
export function track(event: string, props: Props = {}): void {
  if (typeof window === "undefined") return;
  const stamped = baseProps(props);
  // Feed the help assistant's journey context: any search surface fires a
  // `*_search` event carrying { query } (templates, assets, guides).
  if (event.endsWith("_search") && typeof props.query === "string") recordSearch(props.query);
  const uetAction = UET_EVENTS[event];
  if (uetAction) uetEvent(uetAction);
  const ph = getPosthog();
  if (ph && typeof ph.capture === "function") {
    try {
      ph.capture(event, stamped);
    } catch {
      /* analytics must never throw into the UI */
    }
    return;
  }
  pending.push([event, stamped]);
  if (!flushScheduled) {
    flushScheduled = true;
    setTimeout(() => flushPending(), 200);
  }
}
